import React, { useState, useEffect } from "react";
import {
  Card,
  Box,
  Typography,
  Divider,
  TextField,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function SummaryCard({
  multiplier,
  setMultiplier,
  multiplierInput,
  setMultiplierInput,
  visibleRows = [],
  onClearVisibleRows,
  companyName,
  setCompanyName,
  note,
  setNote,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [customFilename, setCustomFilename] = useState("");

  const handleMultiplierChange = (e) => {
    const val = e.target.value;
    setMultiplierInput(val);
    if (val === "") {
      setMultiplier("1");
    } else if (/^\d*\.?\d*$/.test(val)) {
      setMultiplier(val);
    }
  };

  const downloadBlob = (
    content,
    filename,
    type = "application/octet-stream"
  ) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = filename;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const parsedMultiplier = parseFloat(multiplier || "1");

  const values = Object.values(visibleRows);

  const { total, assemblyTotal } = values.reduce(
    (acc, item) => {
      const quantity = parseInt(item.quantity ?? "1", 10);
      const msrp = parseFloat(item.msrp?.replace(/[$,]/g, "") || "0");
      const assembly = parseFloat(item.assembly?.replace(/[$,]/g, "") || "0");

      const unitPrice = msrp * parsedMultiplier;
      const assemblyCost = item.assemblySelected ? assembly * quantity : 0;
      const itemTotal = unitPrice * quantity + assemblyCost;

      acc.total += itemTotal;
      acc.assemblyTotal += assemblyCost;
      return acc;
    },
    { total: 0, assemblyTotal: 0 }
  );

  const formattedTotal = `$${total.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  const formattedAssemblyTotal = `$${assemblyTotal.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 0;

    // Draw black header bar
    doc.setFillColor(0, 0, 0); // black
    doc.rect(0, 0, pageWidth, 25, "F");

    // Add logo centered
    const logoWidth = 50;
    const logoHeight = 15;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoBase64 =
      "data:image/png;base64,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";
    doc.addImage(logoBase64, "PNG", logoX, 5, logoWidth, logoHeight);

    y = 35; // leave space below header

    if (companyName) {
      doc.setFontSize(16);
      const quoteTitle = `Quote for ${companyName}`;
      const pageWidth = doc.internal.pageSize.getWidth();
      const textWidth = doc.getTextWidth(quoteTitle);
      const textX = (pageWidth - textWidth) / 2;
      doc.text(quoteTitle, textX, y);
      y += 10;
    }

    const boxPadding = 4;
    const boxWidth = 180;
    const boxX = 14;

    doc.setFontSize(12);
    const multiplierText = `Multiplier: ${parsedMultiplier}`;
    const multiplierBoxHeight = 10;

    doc.rect(boxX, y, boxWidth, multiplierBoxHeight); // draw box
    doc.text(multiplierText, boxX + boxPadding, y + 7);
    y += multiplierBoxHeight + 5;

    if (note) {
      const noteTitle = "Modification / Special Request:";
      const wrappedNote = doc.splitTextToSize(note, boxWidth - boxPadding * 2);
      const noteHeight = 7 + wrappedNote.length * 6 + 4;

      doc.setTextColor(255, 0, 0);
      doc.setFontSize(12);
      doc.rect(boxX, y, boxWidth, noteHeight); // draw box
      doc.text(noteTitle, boxX + boxPadding, y + 7);
      doc.setFontSize(11);
      doc.text(wrappedNote, boxX + boxPadding, y + 14);
      doc.setTextColor(0, 0, 0);
      y += noteHeight + 5;
    }

    // Price Summary
    const cabinetTotal = values.reduce((sum, item) => {
      const quantity = parseInt(item.quantity ?? "1", 10);
      const msrp = parseFloat(item.msrp?.replace(/[$,]/g, "") || "0");
      const unitPrice = msrp * parsedMultiplier;
      return sum + unitPrice * quantity;
    }, 0);

    const assemblyTotal = values.reduce((sum, item) => {
      const quantity = parseInt(item.quantity ?? "1", 10);
      const assembly = parseFloat(item.assembly?.replace(/[$,]/g, "") || "0");
      return sum + (item.assemblySelected ? assembly * quantity : 0);
    }, 0);

    const grandTotal = cabinetTotal + assemblyTotal;

    const cabinetText = "Cabinets Total:";
    const assemblyText = "Assembly Total:";
    const grandText = "Grand Total:";

    const cabinetValue = `$${cabinetTotal.toFixed(2)}`;
    const assemblyValue = `$${assemblyTotal.toFixed(2)}`;
    const grandValue = `$${grandTotal.toFixed(2)}`;

    const priceBoxHeight = 25;
    const labelX = boxX + boxPadding;
    const valueX = boxX + boxWidth - boxPadding - doc.getTextWidth(grandValue);

    doc.setFontSize(12);
    doc.rect(boxX, y, boxWidth, priceBoxHeight);

    // Line 1: Cabinets Total
    doc.setFont("helvetica", "normal");
    doc.text(cabinetText, labelX, y + 7);
    doc.setFont("helvetica", "bold");
    doc.text(cabinetValue, valueX, y + 7);

    // Line 2: Assembly Total
    doc.setFont("helvetica", "normal");
    doc.text(assemblyText, labelX, y + 14);
    doc.setFont("helvetica", "bold");
    doc.text(assemblyValue, valueX, y + 14);

    // Line 3: Grand Total
    doc.setFont("helvetica", "normal");
    doc.text(grandText, labelX, y + 21);
    doc.setFont("helvetica", "bold");
    doc.text(grandValue, valueX, y + 21);

    // Reset font after box
    doc.setFont("helvetica", "normal");
    y += priceBoxHeight + 5;

    // Table of items
    const headers = [
      [
        "Item",
        "Qty",
        "Unit Price",
        "Assembly Fee (per cabinet)",
        "Assembly",
        "Hinge",
        "Total Price",
      ],
    ];

    const rows = values.map((item) => {
      const quantity = parseInt(item.quantity ?? "1", 10);
      const msrp = parseFloat(item.msrp?.replace(/[$,]/g, "") || "0");
      const assembly = parseFloat(item.assembly?.replace(/[$,]/g, "") || "0");
      const unitPrice = msrp * parsedMultiplier;
      const itemCabinetTotal = unitPrice * quantity;
      const itemAssemblyTotal = item.assemblySelected ? assembly * quantity : 0;
      const itemTotal = itemCabinetTotal + itemAssemblyTotal;

      return [
        `${item.product}-${item.color}-${item.line}`,
        quantity,
        `$${unitPrice.toFixed(2)}`,
        item.assemblySelected ? `$${assembly.toFixed(2)}` : "-",
        item.assemblySelected ? "Yes" : "",
        item.hingeDirection || "-",
        `$${itemTotal.toFixed(2)}`,
      ];
    });

    autoTable(doc, {
      startY: y,
      head: headers,
      body: rows,
      headStyles: {
        fillColor: [50, 50, 50],
        fontStyle: "bold",
      },
    });

    doc.save(`${companyName || "quote"}.pdf`);
  };

  const actuallySaveQuote = (filename) => {
    if (!visibleRows || Object.keys(visibleRows).length === 0) return;

    const dataToSave = {
      visibleRows,
      multiplier,
      companyName,
      note,
      timestamp: new Date().toISOString(),
    };

    const encoded = btoa(
      unescape(encodeURIComponent(JSON.stringify(dataToSave)))
    );

    const finalFilename = filename || "quote";
    downloadBlob(encoded, `${finalFilename}.quote`, "application/x-fq-quote");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const savedMultiplier = localStorage.getItem("quote_multiplier");
    const savedMultiplierInput = localStorage.getItem("quote_multiplierInput");
    const savedCompanyName = localStorage.getItem("quote_companyName");
    const savedNote = localStorage.getItem("quote_note");

    if (savedMultiplier && /^\d*\.?\d*$/.test(savedMultiplier)) {
      setMultiplier(savedMultiplier);

      // 👇 only show user input if it's not the default "1"
      if (savedMultiplier !== "1") {
        setMultiplierInput(savedMultiplierInput ?? savedMultiplier);
      } else {
        setMultiplierInput(""); // make field visually blank
      }
    }

    if (savedCompanyName !== null) setCompanyName(savedCompanyName);
    if (savedNote !== null) setNote(savedNote);
  }, []);

  useEffect(() => {
    localStorage.setItem("quote_multiplier", multiplier);
  }, [multiplier]);

  useEffect(() => {
    localStorage.setItem("quote_multiplierInput", multiplierInput);
  }, [multiplierInput]);

  useEffect(() => {
    localStorage.setItem("quote_companyName", companyName);
  }, [companyName]);

  useEffect(() => {
    localStorage.setItem("quote_note", note);
  }, [note]);

  return (
    <Card variant="outlined">
      <Box sx={{ p: 2 }}>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography
            style={{ fontFamily: "sans-serif", fontWeight: 600 }}
            variant="h4"
            gutterBottom
          >
            Your Quote
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: "red" }}>
          Apply your personal multiplier below to get the total price
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <TextField
          id="multiplier"
          label="Multiplier"
          value={multiplierInput}
          placeholder="Multiplier"
          onChange={handleMultiplierChange}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "#d32f2f",
                borderWidth: "1px",
              },
            },
            "& label.Mui-focused": {
              color: "#d32f2f",
            },
          }}
        />

        <Typography
          variant="body2"
          sx={{ color: "text.secondary", marginBottom: 2 }}
        >
          Enter multiplier as a decimal number such as 0.56, 0.58
        </Typography>

        <Typography
          style={{ fontFamily: "sans-serif", fontWeight: 600 }}
          variant="h6"
          gutterBottom
          sx={{ mt: 3 }}
        >
          Price Breakdown:
        </Typography>

        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Cabinets Total: $
          {(total - assemblyTotal).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Typography>

        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Assembly Total: {formattedAssemblyTotal}
        </Typography>

        <Typography id="price" variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
          Grand Total: {formattedTotal}
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Divider variant="middle" sx={{ mb: 2 }} />
        <TextField
          label="Company Name (optional)"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          fullWidth
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "#d32f2f",
                borderWidth: "1px",
              },
            },
            "& label.Mui-focused": {
              color: "#d32f2f",
            },
          }}
        />

        <TextField
          label="Modification/Special Request"
          multiline
          minRows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Let us know any modifications, custom glass door requests, or special needs."
          fullWidth
          helperText="Note: please use the note section to let us know of any modifications or custom glass door request in addition to any special request. (Subject to additional charges)"
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "#d32f2f",
                borderWidth: "1px",
              },
            },
            "& label.Mui-focused": {
              color: "#d32f2f",
            },
          }}
        />

        <Stack direction="column" spacing={2}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleExportPDF}
            disabled={Object.keys(visibleRows).length === 0}
          >
            Export as PDF
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setSaveDialogOpen(true)}
            disabled={Object.keys(visibleRows).length === 0}
          >
            Save Quote
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={() => setConfirmOpen(true)}
            disabled={Object.keys(visibleRows).length === 0}
          >
            Clear All
          </Button>
          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Confirm Clear</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to remove all items from your list? This
                cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  onClearVisibleRows();
                  setCompanyName("");
                  setNote("");
                  setMultiplier("1");
                  setMultiplierInput("");

                  localStorage.removeItem("quote_multiplier");
                  localStorage.removeItem("quote_multiplierInput");
                  localStorage.removeItem("quote_companyName");
                  localStorage.removeItem("quote_note");

                  setConfirmOpen(false);
                }}
                color="error"
                variant="contained"
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={saveDialogOpen}
            onClose={() => setSaveDialogOpen(false)}
          >
            <DialogTitle>Save Quote</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 1 }}>Save as</DialogContentText>
              <TextField
                autoFocus
                fullWidth
                placeholder={"quote"}
                value={customFilename}
                onChange={(e) => setCustomFilename(e.target.value)}
                label="Filename"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#d32f2f",
                      borderWidth: "1px",
                    },
                  },
                  "& label.Mui-focused": {
                    color: "#d32f2f",
                  },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  actuallySaveQuote(customFilename.trim());
                  setSaveDialogOpen(false);
                  setCustomFilename("");
                }}
                variant="contained"
                color="error"
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Box>
    </Card>
  );
}

export default SummaryCard;
